/**
 * Referral link + start-parameter handling.
 *
 * The contract identifies a referrer by a NUMERIC KEY (not an address): a new
 * buyer's first purchase carries that key as a text-comment, and the contract
 * credits commissions to the owner of that key (see the `receive(str)` handler
 * in Pyramida.tact). So a shareable link must encode the inviter's numeric key,
 * and the app must read it back on launch and pass it into the first buy.
 */
import { getCookie } from '../lib/cookies';

const REF_COOKIE = 'prmd_ref';
const SUB_COOKIE = 'prmd_sub';
/** The ad network's per-click id (postback token), persisted for 30 days. */
export const CID_COOKIE = 'prmd_cid';

/**
 * The longest network click id we accept (Google `gclid` / Facebook `fbclid`
 * regularly exceed 64 and can approach ~100+). Mirrors the backend cap.
 */
const NETWORK_CLICK_MAX = 256;

/**
 * Validates an ad-network per-click id (the postback substitution token).
 * We can't predict a partner's traffic source, so we accept ANY value EXCEPT
 * ASCII control chars (they could inject newlines/NULs downstream) and the
 * literal `{...}` macro markers (an unsubstituted `{click_id}` link must never
 * be stored/sent as a real click id). Mirrors the backend `isValidNetworkClick`.
 */
export function isValidNetworkClick(id: string): boolean {
  if (id.length < 1 || id.length > NETWORK_CLICK_MAX) return false;
  for (let i = 0; i < id.length; i++) {
    const c = id.charCodeAt(i);
    if (c <= 0x1f || c === 0x7f) return false; // ASCII control chars
    if (c === 0x7b || c === 0x7d) return false; // '{' / '}' — unsubstituted macro
  }
  return true;
}

/** Telegram Mini App share base, e.g. `https://t.me/pyramida`. */
const ENV = (import.meta as ImportMeta & {
  readonly env?: Record<string, string | undefined>;
}).env;
const BOT_APP_URL: string =
  ENV?.VITE_TG_BOT_APP_URL?.replace(/\/+$/, '') || 'https://t.me/your_bot/app';

/** AI recruiter bot base, e.g. `https://t.me/prmd_team_bot`. */
const TEAM_BOT_APP_URL: string =
  ENV?.VITE_TEAM_BOT_URL?.replace(/\/+$/, '') || 'https://t.me/prmd_team_bot';

/**
 * Whether a real Telegram bot/app URL has been configured. When it hasn't
 * (the placeholder `your_bot`), the app is running as a plain web deployment,
 * so the Telegram referral link would be a dead `t.me/your_bot` link.
 */
export const HAS_REAL_BOT = !/(^|\/)your_bot(\/|$)/.test(BOT_APP_URL);

/**
 * True when the app is running inside a real Telegram Mini App (launched from
 * the bot), as opposed to a plain browser tab. Telegram populates a non-empty
 * `initData` string only for genuine Mini App launches.
 */
export function isTelegramMiniApp(): boolean {
  const initData = window.Telegram?.WebApp?.initData;
  return typeof initData === 'string' && initData.length > 0;
}

/**
 * Highest invite key the registrar has actually issued (`nextInviteKey - 1`),
 * fed from the app-state poll. A key beyond it belongs to no one: on-chain it
 * would resolve to null and register the buyer with NO referrer at all, so
 * such keys are downgraded to the owner key 0 before any transaction.
 */
let lastIssuedKey: number | null = null;

export function setLastIssuedKey(key: number): void {
  if (Number.isFinite(key) && key > 0) lastIssuedKey = key;
}

/** Clamps a not-yet-issued referral key to the owner key 0. */
export function sanitizeReferrerKey(key: number | null): number | null {
  if (key === null) return null;
  if (lastIssuedKey !== null && key > lastIssuedKey) return 0;
  return key;
}

/**
 * Reads the referrer key the app was opened with, from either the Telegram
 * `start_param` (Mini App) or a `?startapp=` / `?ref=` URL query (browser), and
 * finally the persisted 30-day `prmd_ref` cookie (so a partner link keeps
 * attributing on later same-browser visits without the query). URL/Telegram
 * always win over the cookie. Returns a non-negative integer key (0 = owner), or null.
 */
export function getIncomingReferrerKey(): number | null {
  const candidates: (string | undefined | null)[] = [
    window.Telegram?.WebApp?.initDataUnsafe?.start_param,
    new URLSearchParams(window.location.search).get('startapp'),
    new URLSearchParams(window.location.search).get('ref'),
    getCookie(REF_COOKIE),
  ];
  for (const raw of candidates) {
    if (!raw) continue;
    // Accept a bare numeric key OR the leading numeric key of a combined
    // `<key>_<group>_<click_id>` payload, so a mangled group/click id (or a bot
    // deep-link value that leaks into `startapp`) never hides a readable key.
    const m = /^(\d+)(?:_|$)/.exec(raw);
    if (!m) continue;
    const key = Number(m[1]);
    if (Number.isInteger(key) && key >= 0) return sanitizeReferrerKey(key);
  }
  return null;
}

/**
 * The bot deep link that carries the visitor's live attribution forward, using
 * the same `{ref_key}[_{source}[_{click_id}]]` /start grammar partner links
 * use. The key comes from the URL or the 30-day attribution cookie (whichever
 * `getIncomingReferrerKey` resolves), so an expired attribution simply yields
 * the plain bot link.
 */
export function buildAttributedBotLink(): string {
  const key = getIncomingReferrerKey();
  if (key == null) return BOT_APP_URL;
  // The bot's /start grammar splits on `_` and wants an alnum source, so only
  // forward a sub/cid that survive the round-trip; the key always goes through.
  const sub = getIncomingSubId();
  if (!sub || !/^[A-Za-z0-9]+$/.test(sub)) return `${BOT_APP_URL}?start=${key}`;
  const cid = getIncomingClickId();
  const cidOk = cid && /^[A-Za-z0-9-]+$/.test(cid) ? `_${cid}` : '';
  // Telegram caps a /start payload at 64 chars: drop the cid (then the sub)
  // rather than ship a link Telegram refuses.
  let payload = `${key}_${sub}${cidOk}`;
  if (payload.length > 64) payload = `${key}_${sub}`;
  if (payload.length > 64) payload = String(key);
  return `${BOT_APP_URL}?start=${payload}`;
}

/**
 * The traffic SOURCE label of a Telegram deep-link launch: the middle token of
 * a combined `<key>_<group>[_<click_id>]` `start_param` payload (the same
 * grammar as the bot's /start tracking links). Used to segment GA by source
 * inside Telegram, mirroring the web `?sub=` label. Null for bare-key or
 * absent payloads.
 */
export function getTelegramStartSource(): string | null {
  const raw = window.Telegram?.WebApp?.initDataUnsafe?.start_param;
  if (!raw) return null;
  const m = /^\d+_([A-Za-z0-9-]{1,64})(?:_|$)/.exec(raw);
  return m ? m[1].toLowerCase() : null;
}

/**
 * The app tab the bot asked to open, from a `?screen=` query the bot appends to
 * the Mini App URL (e.g. `?screen=buy`). Telegram opens `web_app.url` verbatim,
 * so the query is present in `window.location.search`. `buy`/`lottery` both map
 * to the lottery (buy) tab. Returns null when absent or unrecognized, so the app
 * falls back to its default tab.
 */
export function getIncomingScreen(): 'lottery' | 'sand' | 'referrals' | 'tasks' | 'jetton' | 'about' | null {
  const raw = new URLSearchParams(window.location.search).get('screen')?.toLowerCase();
  switch (raw) {
    case 'buy':
    case 'lottery':
      return 'lottery';
    case 'sand':
      return 'sand';
    case 'referrals':
    case 'invite':
    case 'story':
      return 'referrals';
    case 'tasks':
    case 'quests':
      return 'tasks';
    case 'jetton':
    case 'prmd':
      return 'jetton';
    case 'about':
      return 'about';
    default:
      return null;
  }
}

/**
 * When the bot opens the Mini App with `?screen=story&key=<refKey>` (the bot's
 * "share to story" button), returns that positive numeric key so the app can
 * auto-launch the Telegram story composer for it. Falls back to the Mini App
 * `start_param`. Returns null when this isn't a story-share launch.
 */
export function getStoryShareKey(): number | null {
  const params = new URLSearchParams(window.location.search);
  if ((params.get('screen') ?? '').toLowerCase() !== 'story') return null;
  const raw = params.get('key') ?? window.Telegram?.WebApp?.initDataUnsafe?.start_param;
  if (!raw || !/^\d+$/.test(raw)) return null;
  const key = Number(raw);
  return Number.isInteger(key) && key >= 0 ? key : null;
}

/**
 * Which bot a `?screen=story` launch should post an invite for: `'team'` when
 * the bot opened the composer with `&bot=team` (the "invite into the AI bot"
 * story), otherwise `'main'`. The QR image + widget link are chosen accordingly.
 */
export function getStoryShareBot(): 'main' | 'team' {
  const raw = new URLSearchParams(window.location.search).get('bot')?.toLowerCase();
  return raw === 'team' ? 'team' : 'main';
}

/**
 * Telegram referral link: `t.me/<bot>?start=<key>`. Opening it in Telegram
 * starts the bot with `/start <key>`, which records the inviter against the
 * new user's Telegram account; the Mini App later reads it back (GET /referrer)
 * and attaches it on the first ticket purchase. Returns null when no real bot
 * is configured.
 */
export function buildTelegramReferralLink(referrerKey: number): string | null {
  if (!HAS_REAL_BOT) return null;
  return `${BOT_APP_URL}?start=${referrerKey}`;
}

/**
 * AI-recruiter-bot referral link: `t.me/<team_bot>?start=<key>`. Invitees who
 * open it are onboarded/coached by the AI instructor, and the SAME numeric key
 * still attaches on-chain (the team bot records it in the shared cross-bot
 * `referral_intake` table) — so referral earnings are identical to a main-bot
 * invite. Always available (the team bot handle has a sensible default).
 */
export function buildTeamTelegramReferralLink(referrerKey: number): string {
  return `${TEAM_BOT_APP_URL}?start=${referrerKey}`;
}

/**
 * The traffic SOURCE label read back from a web referral link (`?sub=<source>`),
 * used to group web partner traffic per source (e.g. 'main'/'fb1'). Returns null
 * when absent/invalid. Accepts a permissive charset (the backend lower-cases and
 * falls back to 'main' for anything non-alnum), so legacy links keep working.
 */
export function getIncomingSubId(): string | null {
  const re = /^[A-Za-z0-9_-]{1,64}$/;
  const params = new URLSearchParams(window.location.search);
  const ref = params.get('ref');
  if (ref && /^(?:0|[1-9]\d*)$/.test(ref)) {
    const url = params.get('sub');
    return url && re.test(url) ? url : 'main';
  }
  const cookie = getCookie(SUB_COOKIE);
  return cookie && re.test(cookie) ? cookie : null;
}

/**
 * The ad network's per-click id read back from `?cid=<click_id>`. This is the
 * postback substitution token — separate from the SOURCE label so a source
 * groups many clicks. A valid incoming value is accepted only as part of a valid
 * referral tuple; `persistAttribution` stores it in the 30-day `prmd_cid` cookie.
 * Falls back to that cookie after clean navigation and never returns the
 * browser-minted internal click id.
 */
export function getIncomingClickId(): string | null {
  const params = new URLSearchParams(window.location.search);
  const ref = params.get('ref');
  const url = params.get('cid');
  if (ref && /^(?:0|[1-9]\d*)$/.test(ref)) return url && isValidNetworkClick(url) ? url : null;
  const cookie = getCookie(CID_COOKIE);
  return cookie && isValidNetworkClick(cookie) ? cookie : null;
}

/**
 * Plain-web referral link: the current site origin with `?ref=<key>`, which
 * {@link getIncomingReferrerKey} reads back on launch. When a `source` is given
 * it is appended as `&sub=<source>&cid={click_id}` — the source groups the
 * traffic while the ad network stamps its own click id into `{click_id}` for
 * postback substitution, mirroring the bot deep link.
 */
export function buildSiteReferralLink(
  referrerKey: number,
  source?: string,
  opts?: { clickId?: boolean },
): string {
  const origin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : '';
  const src = source?.trim();
  const cid = opts?.clickId === false ? '' : '&cid={click_id}';
  return `${origin}/?ref=${referrerKey}${src ? `&sub=${src}${cid}` : ''}`;
}

/**
 * Builds the single best shareable link for a referrer key — the Telegram
 * Mini App deep link when a bot is configured, otherwise the site link.
 */
export function buildReferralLink(referrerKey: number): string {
  return buildTelegramReferralLink(referrerKey) ?? buildSiteReferralLink(referrerKey);
}

/**
 * Partner tracking deep link: `t.me/<bot>?start=<key>_<source>_{click_id}`. The
 * middle token is the traffic SOURCE label (grouping dimension, e.g. 'main'/'fb1')
 * and the trailing `{click_id}` macro is stamped by the ad network per click
 * (postback substitution token). The bot attributes the visitor to the key's
 * 'referrer' campaign (per-source stats + postbacks) AND records the referral
 * key, so on-chain earnings attach as usual.
 */
export function buildPartnerTrackingLink(
  referrerKey: number,
  source: string,
  opts?: { clickId?: boolean },
): string {
  const cid = opts?.clickId === false ? '' : '_{click_id}';
  return `${BOT_APP_URL}?start=${referrerKey}_${source}${cid}`;
}

/**
 * Direct Mini App tracking deep link: `t.me/<bot>?startapp=<key>_<source>_{click_id}`.
 * Opens the Mini App IMMEDIATELY (no bot /start step) — Telegram signs the
 * payload into `initData.start_param`, the app reads the key/source on launch
 * and the bot attributes the visitor server-side from the signed payload on
 * `/app-opened` (same rules as a /start arrival: intake ledger + campaign
 * stats + postbacks). Best for ad traffic where every extra tap costs users.
 */
export function buildStartappTrackingLink(
  referrerKey: number,
  source: string,
  opts?: { clickId?: boolean },
): string {
  const cid = opts?.clickId === false ? '' : '_{click_id}';
  return `${BOT_APP_URL}?startapp=${referrerKey}_${source}${cid}`;
}

/** Same tracking link, but landing the visitor in the AI recruiter bot. */
export function buildPartnerTeamTrackingLink(
  referrerKey: number,
  source: string,
  opts?: { clickId?: boolean },
): string {
  const cid = opts?.clickId === false ? '' : '_{click_id}';
  return `${TEAM_BOT_APP_URL}?start=${referrerKey}_${source}${cid}`;
}

/**
 * Partner-to-partner invite: the /partner page itself with `?ref=<key>`, so
 * partners can recruit other webmasters straight into the affiliate panel
 * (the invitee's first purchase attaches the key on-chain as usual). `source`
 * behaves exactly as in {@link buildSiteReferralLink}.
 */
export function buildPartnerPageLink(
  referrerKey: number,
  source?: string,
  opts?: { clickId?: boolean },
): string {
  const origin =
    typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
  const src = source?.trim();
  const cid = opts?.clickId === false ? '' : '&cid={click_id}';
  return `${origin}/partner?ref=${referrerKey}${src ? `&sub=${src}${cid}` : ''}`;
}

/** Friendly invite caption shared alongside the referral link / QR image. */
export const SHARE_CAPTION =
  'Играй в еженедельную лотерею Pyramida на TON и получай токены PRMD за каждое участие!';

/** Builds a `t.me/share/url` deep link with a friendly invite caption. */
export function buildTelegramShareLink(referrerKey: number, url?: string, caption?: string): string {
  const link = url ?? buildReferralLink(referrerKey);
  const text = caption ?? SHARE_CAPTION;
  return `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`;
}
